import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import 'bulma/css/bulma.min.css';
import { InfoService } from '../../services/site/InfoService';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const scaleIn = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
}> = ({ children, className = '' }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// ============================================
// ABOUT US PAGE
// ============================================

const AboutHeroSection: React.FC = () => {
  return (
    <section className="hdt-about-hero section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="title is-1 hdt-hero-title">
                Transforming Businesses with Intelligent Technology
              </h1>
              <p className="subtitle is-4 hdt-hero-subtitle mb-5">
                We're HDT Solutions  - Your Partner in Digital Innovation
              </p>
              <p className="content is-size-5 mb-5">
                Since 2018, we've been helping businesses automate workflows, enhance security, 
                and leverage AI to stay ahead in the digital age. Our mission is simple: make 
                cutting-edge technology accessible and affordable for every business.
              </p>
              <div className="buttons">
                <motion.a 
                 href={InfoService.loginLink}
                  className="button is-primary is-large"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get Started
                </motion.a>
                <motion.a 
                href="/"
                  className="button is-outlined is-primary is-large"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Our Services
                </motion.a>
              </div>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hdt-hero-visual"
            >
              <div className="hdt-floating-icons">
                <motion.div className="hdt-icon-circle" animate={{ y: [0, -20, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                  🤖
                </motion.div>
                <motion.div className="hdt-icon-circle" animate={{ y: [0, -15, 0] }} transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}>
                  🔐
                </motion.div>
                <motion.div className="hdt-icon-circle" animate={{ y: [0, -25, 0] }} transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}>
                  📊
                </motion.div>
                <motion.div className="hdt-icon-circle" animate={{ y: [0, -18, 0] }} transition={{ duration: 2.8, repeat: Infinity, delay: 1.5 }}>
                  💬
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatsSection: React.FC = () => {
  const stats = [
    { number: '500+', label: 'Happy Clients', icon: '😊', color: '#2196F3' },
    { number: '50K+', label: 'Documents Processed', icon: '📄', color: '#E91E63' },
    { number: '99.9%', label: 'Uptime Guarantee', icon: '⚡', color: '#4CAF50' },
    { number: '24/7', label: 'Support Available', icon: '🎯', color: '#9C27B0' }
  ];

  return (
    <AnimatedSection className="hdt-stats-section section">
      <div className="container">
        <motion.div 
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {stats.map((stat, index) => (
            <motion.div key={index} className="column is-3" variants={scaleIn}>
              <div className="hdt-stat-card" style={{ borderTopColor: stat.color }}>
                <div className="hdt-stat-icon" style={{ background: stat.color }}>
                  {stat.icon}
                </div>
                <h3 className="hdt-stat-number">{stat.number}</h3>
                <p className="hdt-stat-label">{stat.label}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

const MissionVisionSection: React.FC = () => {
  return (
    <AnimatedSection className="hdt-mission-section section">
      <div className="container">
        <div className="columns">
          <div className="column is-6">
            <motion.div 
              className="hdt-mission-card"
              whileHover={{ y: -5 }}
            >
              <div className="hdt-mission-icon">🎯</div>
              <h2 className="title is-3 mb-4">Our Mission</h2>
              <p className="content is-size-5">
                To empower businesses of all sizes with intelligent automation and security solutions 
                that drive growth, efficiency, and innovation. We believe technology should work for you, 
                not against you.
              </p>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div 
              className="hdt-vision-card"
              whileHover={{ y: -5 }}
            >
              <div className="hdt-vision-icon">🚀</div>
              <h2 className="title is-3 mb-4">Our Vision</h2>
              <p className="content is-size-5">
                To become India's most trusted technology partner, known for delivering 
                cutting-edge solutions with zero downtime, exceptional support, and measurable results 
                that transform how businesses operate.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

const ValuesSection: React.FC = () => {
  const values = [
    { icon: '💎', title: 'Quality First', desc: 'We never compromise on quality. Every solution is tested, refined, and perfected.', color: '#2196F3' },
    { icon: '🤝', title: 'Customer Success', desc: 'Your success is our success. We go the extra mile to ensure you achieve your goals.', color: '#E91E63' },
    { icon: '⚡', title: 'Innovation', desc: 'We stay ahead of technology trends to bring you the latest and most effective solutions.', color: '#FF9800' },
    { icon: '🔒', title: 'Security', desc: 'Your data security is paramount. We implement industry-leading security standards.', color: '#9C27B0' },
    { icon: '🎓', title: 'Transparency', desc: 'No hidden costs, no surprises. We believe in honest, transparent communication.', color: '#4CAF50' },
    { icon: '⏱️', title: 'Reliability', desc: 'Count on us 24/7. We deliver on time, every time, with zero downtime.', color: '#00BCD4' }
  ];

  return (
    <AnimatedSection className="hdt-values-section section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2">Our Core Values</h2>
          <p className="subtitle is-5">The principles that guide everything we do</p>
        </div>
        <motion.div 
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {values.map((value, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div 
                className="hdt-value-card"
                whileHover={{ scale: 1.05 }}
              >
                <div className="hdt-value-icon" style={{ background: value.color }}>
                  {value.icon}
                </div>
                <h4 className="title is-5 mb-3">{value.title}</h4>
                <p>{value.desc}</p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

const TeamSection: React.FC = () => {
  const team = [
    { name: 'Tech Experts', role: '15+ Engineers', icon: '👨‍💻', color: '#2196F3' },
    { name: 'AI Specialists', role: '8+ Data Scientists', icon: '🤖', color: '#E91E63' },
    { name: 'Security Team', role: '5+ Certified Experts', icon: '🛡️', color: '#9C27B0' },
    { name: 'Support Team', role: '24/7 Availability', icon: '💬', color: '#4CAF50' }
  ];

  return (
    <AnimatedSection className="hdt-team-section section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2">Meet Our Team</h2>
          <p className="subtitle is-5">Passionate experts dedicated to your success</p>
        </div>
        <motion.div 
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {team.map((member, index) => (
            <motion.div key={index} className="column" variants={scaleIn}>
              <div className="hdt-team-card">
                <div className="hdt-team-avatar" style={{ background: member.color }}>
                  {member.icon}
                </div>
                <h4 className="title is-5 mb-2">{member.name}</h4>
                <p className="has-text-grey">{member.role}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// ============================================
// PRICING PAGE
// ============================================

const PricingHeroSection: React.FC = () => {
  return (
    <section className="hdt-pricing-hero section">
      <div className="container">
        <div className="has-text-centered">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="title is-1 hdt-pricing-title">
              Simple, Transparent Pricing
            </h1>
            <p className="subtitle is-4 mb-5">
              Choose the perfect plan for your business. No hidden fees, no surprises.
            </p>
            <div className="hdt-pricing-badges">
              <span className="tag is-large hdt-badge-primary">💰 Flexible Plans</span>
              <span className="tag is-large hdt-badge-success">✅ No Setup Fee</span>
              <span className="tag is-large hdt-badge-info">🎁 Free Trial Available</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const PricingCardsSection: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  const services = [
    {
      id: 'ai-extraction',
      name: 'AI Data Extraction',
      icon: '🤖',
      description: 'Extract data from documents automatically with AI',
      color: '#2196F3',
      features: [
        'Extract from PDFs, Images, Scans',
        'Support for 50+ document types',
        'Custom field extraction',
        'Batch processing',
        'API access',
        '99.9% accuracy guarantee',
        'Real-time processing',
        'Export to Excel/JSON/CSV'
      ],
      pricing: {
        monthly: { starter: 2999, professional: 7999, enterprise: 'Custom' },
        annual: { starter: 29990, professional: 79990, enterprise: 'Custom' }
      },
      limits: {
        starter: '500 documents/month',
        professional: '5,000 documents/month',
        enterprise: 'Unlimited'
      }
    },
    {
      id: 'chatbot',
      name: 'Intelligent Chatbot',
      icon: '💬',
      description: '24/7 AI-powered customer support automation',
      color: '#E91E63',
      features: [
        'Natural language understanding',
        'Multi-language support',
        'Website integration',
        'WhatsApp/Telegram support',
        'Custom training on your data',
        'Analytics dashboard',
        'Lead capture & qualification',
        'CRM integration'
      ],
      pricing: {
        monthly: { starter: 3999, professional: 9999, enterprise: 'Custom' },
        annual: { starter: 39990, professional: 99990, enterprise: 'Custom' }
      },
      limits: {
        starter: '1,000 conversations/month',
        professional: '10,000 conversations/month',
        enterprise: 'Unlimited'
      }
    },
    {
      id: 'digital-signature',
      name: 'Digital Signature',
      icon: '✍️',
      description: 'Secure electronic document signing solution',
      color: '#9C27B0',
      features: [
        'Legally valid e-signatures',
        'Multi-party signing',
        'Document templates',
        'Audit trail & compliance',
        'Mobile signing support',
        'Bulk signing',
        'Email notifications',
        'Cloud storage integration'
      ],
      pricing: {
        monthly: { starter: 1999, professional: 4999, enterprise: 'Custom' },
        annual: { starter: 19990, professional: 49990, enterprise: 'Custom' }
      },
      limits: {
        starter: '100 signatures/month',
        professional: '1,000 signatures/month',
        enterprise: 'Unlimited'
      }
    },
    {
      id: 'templates',
      name: 'PDF/Word Templates',
      icon: '📄',
      description: 'Automated document generation from templates',
      color: '#FF9800',
      features: [
        'Dynamic template creation',
        'Auto-fill from database',
        'PDF & Word support',
        'Custom branding',
        'Merge multiple documents',
        'Batch generation',
        'Version control',
        'API integration'
      ],
      pricing: {
        monthly: { starter: 1499, professional: 3999, enterprise: 'Custom' },
        annual: { starter: 14990, professional: 39990, enterprise: 'Custom' }
      },
      limits: {
        starter: '200 documents/month',
        professional: '2,000 documents/month',
        enterprise: 'Unlimited'
      }
    },
    {
      id: 'security',
      name: 'Content Security Policy',
      icon: '🔒',
      description: 'Protect your website from cyber threats',
      color: '#4CAF50',
      features: [
        'Zero downtime implementation',
        'Block XSS & injection attacks',
        'Real-time threat monitoring',
        'Security score improvement',
        'Compliance support',
        '24/7 security updates',
        'Vulnerability scanning',
        'Incident response'
      ],
      pricing: {
        monthly: { starter: 4999, professional: 9999, enterprise: 'Custom' },
        annual: { starter: 49990, professional: 99990, enterprise: 'Custom' }
      },
      limits: {
        starter: '1 website',
        professional: '5 websites',
        enterprise: 'Unlimited websites'
      }
    }
  ];

  const getPrice = (service: typeof services[0], plan: 'starter' | 'professional' | 'enterprise') => {
    const price = service.pricing[billingCycle][plan];
    if (typeof price === 'number') {
      return billingCycle === 'annual' 
        ? `₹${(price / 12).toLocaleString('en-IN')}/mo`
        : `₹${price.toLocaleString('en-IN')}/mo`;
    }
    return price;
  };

  return (
    <section className="hdt-pricing-cards section">
      <div className="container">
        {/* Billing Toggle */}
        <div className="hdt-billing-toggle has-text-centered mb-6">
          <div className="buttons is-centered">
            <button 
              className={`button is-large ${billingCycle === 'monthly' ? 'is-primary' : 'is-light'}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button 
              className={`button is-large ${billingCycle === 'annual' ? 'is-primary' : 'is-light'}`}
              onClick={() => setBillingCycle('annual')}
            >
              Annual
              <span className="tag is-success ml-2">Save 17%</span>
            </button>
          </div>
        </div>

        {/* Service Cards */}
        {services.map((service, index) => (
          <AnimatedSection key={service.id} className="mb-6">
            <div className="hdt-service-pricing-wrapper">
              <div className="hdt-service-header" style={{ background: service.color }}>
                <div className="hdt-service-icon-large">{service.icon}</div>
                <h3 className="title is-3 has-text-white mb-2">{service.name}</h3>
                <p className="has-text-white-ter">{service.description}</p>
              </div>

              <div className="columns is-variable is-4">
                {/* Starter Plan */}
                <div className="column">
                  <motion.div 
                    className="hdt-pricing-card"
                    whileHover={{ y: -8 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="hdt-plan-badge">Starter</div>
                    <div className="hdt-plan-price">
                      {getPrice(service, 'starter')}
                      {billingCycle === 'annual' && typeof service.pricing.annual.starter === 'number' && (
                        <div className="hdt-plan-annual-price">
                          ₹{service.pricing.annual.starter.toLocaleString('en-IN')}/year
                        </div>
                      )}
                    </div>
                    <p className="hdt-plan-limit">{service.limits.starter}</p>
                    <ul className="hdt-feature-list">
                      {service.features.slice(0, 4).map((feature, i) => (
                        <li key={i}>
                          <span className="icon has-text-success">
                            <i className="fas fa-check-circle"></i>
                          </span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <motion.a
                      href={`tel:+91 9553896536`}
                      className="button is-fullwidth is-outlined"
                      style={{ borderColor: service.color, color: service.color }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Get Started
                    </motion.a>
                  </motion.div>
                </div>

                {/* Professional Plan */}
                <div className="column">
                  <motion.div 
                    className="hdt-pricing-card hdt-pricing-card-featured"
                    style={{ borderColor: service.color }}
                    whileHover={{ y: -8 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="hdt-plan-badge hdt-plan-badge-popular" style={{ background: service.color }}>
                      Most Popular
                    </div>
                    <div className="hdt-plan-price">
                      {getPrice(service, 'professional')}
                      {billingCycle === 'annual' && typeof service.pricing.annual.professional === 'number' && (
                        <div className="hdt-plan-annual-price">
                          ₹{service.pricing.annual.professional.toLocaleString('en-IN')}/year
                        </div>
                      )}
                    </div>
                    <p className="hdt-plan-limit">{service.limits.professional}</p>
                    <ul className="hdt-feature-list">
                      {service.features.map((feature, i) => (
                        <li key={i}>
                          <span className="icon has-text-success">
                            <i className="fas fa-check-circle"></i>
                          </span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <motion.a
                      href={`tel:+91 9553896536`}
                      className="button is-fullwidth"
                      style={{ background: service.color, color: 'white' }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Get Started
                    </motion.a>
                  </motion.div>
                </div>

                {/* Enterprise Plan */}
                <div className="column">
                  <motion.div 
                    className="hdt-pricing-card"
                    whileHover={{ y: -8 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="hdt-plan-badge">Enterprise</div>
                    <div className="hdt-plan-price">Custom</div>
                    <p className="hdt-plan-limit">{service.limits.enterprise}</p>
                    <ul className="hdt-feature-list">
                      {service.features.map((feature, i) => (
                        <li key={i}>
                          <span className="icon has-text-success">
                            <i className="fas fa-check-circle"></i>
                          </span>
                          <span>{feature}</span>
                        </li>
                      ))}
                      <li>
                        <span className="icon has-text-success">
                          <i className="fas fa-check-circle"></i>
                        </span>
                        <span>Dedicated account manager</span>
                      </li>
                      <li>
                        <span className="icon has-text-success">
                          <i className="fas fa-check-circle"></i>
                        </span>
                        <span>Custom integrations</span>
                      </li>
                      <li>
                        <span className="icon has-text-success">
                          <i className="fas fa-check-circle"></i>
                        </span>
                        <span>Priority support</span>
                      </li>
                    </ul>
                    <motion.a
                      href={`mailto:sales@hdtsolution.com`}
                      className="button is-fullwidth is-dark"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Contact Sales
                    </motion.a>
                  </motion.div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        ))}
      </div>
    </section>
  );
};

const BundlePricingSection: React.FC = () => {
  const bundles = [
    {
      name: 'Startup Bundle',
      icon: '🚀',
      color: '#2196F3',
      description: 'Perfect for startups and small businesses',
      services: ['AI Data Extraction (Starter)', 'Digital Signature (Starter)', 'PDF Templates (Starter)'],
      regularPrice: 6497,
      bundlePrice: 4999,
      savings: 1498
    },
    {
      name: 'Business Bundle',
      icon: '💼',
      color: '#E91E63',
      description: 'Ideal for growing businesses',
      services: ['AI Data Extraction (Pro)', 'Chatbot (Pro)', 'Digital Signature (Pro)', 'PDF Templates (Pro)'],
      regularPrice: 26996,
      bundlePrice: 19999,
      savings: 6997,
      popular: true
    },
    {
      name: 'Enterprise Bundle',
      icon: '🏢',
      color: '#9C27B0',
      description: 'Complete solution for enterprises',
      services: ['All Services (Pro)', 'Content Security Policy', 'Priority Support', 'Custom Integrations'],
      regularPrice: 'Custom',
      bundlePrice: 'Contact Us',
      savings: 'Up to 40%'
    }
  ];

  return (
    <AnimatedSection className="hdt-bundle-section section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2">Bundle & Save More</h2>
          <p className="subtitle is-5">Combine services for maximum value</p>
        </div>
        <motion.div 
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {bundles.map((bundle, index) => (
            <motion.div key={index} className="column" variants={scaleIn}>
              <motion.div 
                className={`hdt-bundle-card ${bundle.popular ? 'hdt-bundle-featured' : ''}`}
                style={{ borderColor: bundle.color }}
                whileHover={{ y: -10 }}
              >
                {bundle.popular && (
                  <div className="hdt-bundle-popular-badge" style={{ background: bundle.color }}>
                    BEST VALUE
                  </div>
                )}
                <div className="hdt-bundle-icon" style={{ background: bundle.color }}>
                  {bundle.icon}
                </div>
                <h3 className="title is-4 mb-2">{bundle.name}</h3>
                <p className="has-text-grey mb-4">{bundle.description}</p>
                <div className="hdt-bundle-price-box">
                  {typeof bundle.bundlePrice === 'number' ? (
                    <>
                      <p className="hdt-bundle-regular-price">₹{bundle.regularPrice.toLocaleString('en-IN')}/mo</p>
                      <p className="hdt-bundle-price">₹{bundle.bundlePrice.toLocaleString('en-IN')}/mo</p>
                      <p className="hdt-bundle-savings" style={{ color: bundle.color }}>
                        Save ₹{bundle.savings.toLocaleString('en-IN')}/mo
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="hdt-bundle-price">{bundle.bundlePrice}</p>
                      <p className="hdt-bundle-savings" style={{ color: bundle.color }}>
                        {bundle.savings}
                      </p>
                    </>
                  )}
                </div>
                <ul className="hdt-bundle-services mb-4">
                  {bundle.services.map((service, i) => (
                    <li key={i}>
                      <span className="icon has-text-success">
                        <i className="fas fa-check-circle"></i>
                      </span>
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
                <motion.a
                  href={typeof bundle.bundlePrice === 'number' ? "tel:+91 9553896536" : "mailto:sales@hdtsolution.com"}
                  className="button is-fullwidth"
                  style={{ background: bundle.color, color: 'white' }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {typeof bundle.bundlePrice === 'number' ? 'Get Started' : 'Contact Sales'}
                </motion.a>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

const FAQPricingSection: React.FC = () => {
  const faqs = [
    {
      question: "Can I try before I buy?",
      answer: "Yes! We offer a 14-day free trial for all services (except Enterprise plans). No credit card required."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit/debit cards, UPI, net banking, and bank transfers. For annual plans, we also offer invoice-based payments."
    },
    {
      question: "Can I upgrade or downgrade my plan?",
      answer: "Absolutely! You can upgrade or downgrade anytime. We'll prorate the difference and adjust your next billing cycle accordingly."
    },
    {
      question: "Is there a setup fee?",
      answer: "No setup fees for any plan! We believe in transparent pricing with no hidden costs."
    },
    {
      question: "What happens if I exceed my plan limits?",
      answer: "We'll notify you when you reach 80% of your limit. You can either upgrade your plan or pay for overage at a small additional fee."
    },
    {
      question: "Do you offer refunds?",
      answer: "Yes! We offer a 30-day money-back guarantee for all plans. If you're not satisfied, we'll refund your payment, no questions asked."
    }
  ];

  return (
    <AnimatedSection className="hdt-faq-pricing section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2">Frequently Asked Questions</h2>
        </div>
        <div className="columns is-centered">
          <div className="column is-10">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                className="box hdt-faq-box mb-4"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h4 className="title is-5 mb-3">
                  <span className="hdt-faq-number">{index + 1}.</span> {faq.question}
                </h4>
                <p className="has-text-grey-dark">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

const CTASection: React.FC = () => {
  return (
    <section className="hdt-cta-section section">
      <div className="container">
        <div className="box hdt-cta-box">
          <div className="has-text-centered">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="title is-2 mb-4">Ready to Get Started?</h2>
              <p className="subtitle is-5 mb-5">
                Join 500+ businesses already using HDT Solutions
              </p>
              <div className="buttons is-centered are-large">
                <motion.a 
                  href="tel:+91 9553896536" 
                  className="button is-primary is-large"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-phone"></i>
                  </span>
                  <span>Call: {InfoService?.mobile_no_1}</span>
                </motion.a>
                <motion.a 
                  href="mailto:sales@hdtsolution.com" 
                  className="button is-link is-large"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <span>Email Us</span>
                </motion.a>
              </div>
              <p className="mt-4 has-text-white">
                <span className="icon has-text-success">
                  <i className="fas fa-shield-alt"></i>
                </span>
                30-day money-back guarantee • No setup fees • Cancel anytime
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main App Component with Navigation
export default function AboutUs() {
  const [currentPage, setCurrentPage] = useState<'about' | 'pricing'>('about');

  return (
    <div className="hdt-pages-wrapper">
       <div>
          <AboutHeroSection />
          <StatsSection />
          <MissionVisionSection />
          <ValuesSection />
          <TeamSection />
          <CTASection />
        </div>
      {/* Styles */}
      <style>{`
        .hdt-pages-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        }

        /* Navigation */
        .hdt-nav {
          background: linear-gradient(135deg, #1e3c72 0%, #2196F3 100%);
          padding: 1rem 0;
          position: sticky;
          top: 0;
          z-index: 100;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .hdt-nav-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .hdt-nav-links {
          display: flex;
          gap: 1rem;
        }

        /* About Us Styles */
        .hdt-about-hero {
          background: linear-gradient(135deg, #2196F3 0%, #1e3c72 100%);
          padding: 4rem 0;
          color: white;
        }

        .hdt-hero-title {
          color: white !important;
          font-size: 3rem !important;
          font-weight: 800 !important;
          line-height: 1.2;
        }

        .hdt-hero-subtitle {
          color: #E3F2FD !important;
          font-size: 1.5rem !important;
        }

        .hdt-hero-visual {
          position: relative;
          height: 400px;
        }

        .hdt-floating-icons {
          position: relative;
          width: 100%;
          height: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          padding: 2rem;
        }

        .hdt-icon-circle {
          background: rgba(255,255,255,0.2);
          backdrop-filter: blur(10px);
          border-radius: 50%;
          width: 120px;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 4rem;
          box-shadow: 0 8px 16px rgba(0,0,0,0.2);
        }

        /* Stats Section */
        .hdt-stats-section {
          padding: 3rem 0;
        }

        .hdt-stat-card {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          border-top: 4px solid;
          transition: transform 0.3s;
        }

        .hdt-stat-card:hover {
          transform: translateY(-5px);
        }

        .hdt-stat-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          margin: 0 auto 1rem;
        }

        .hdt-stat-number {
          font-size: 3rem;
          font-weight: 800;
          color: #1e3c72;
          margin-bottom: 0.5rem;
        }

        .hdt-stat-label {
          font-size: 1.1rem;
          color: #666;
        }

        /* Mission/Vision Section */
        .hdt-mission-section {
          background: white;
          padding: 4rem 0;
        }

        .hdt-mission-card, .hdt-vision-card {
          background: linear-gradient(135deg, #E3F2FD 0%, #fff 100%);
          border-radius: 16px;
          padding: 3rem;
          height: 100%;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          transition: transform 0.3s;
        }

        .hdt-vision-card {
          background: linear-gradient(135deg, #FCE4EC 0%, #fff 100%);
        }

        .hdt-mission-icon, .hdt-vision-icon {
          font-size: 4rem;
          margin-bottom: 1.5rem;
        }

        /* Values Section */
        .hdt-values-section {
          padding: 4rem 0;
        }

        .hdt-value-card {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          height: 100%;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: transform 0.3s;
        }

        .hdt-value-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          margin: 0 auto 1.5rem;
          color: white;
        }

        /* Team Section */
        .hdt-team-section {
          background: white;
          padding: 4rem 0;
        }

        .hdt-team-card {
          background: #f5f7fa;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
        }

        .hdt-team-avatar {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 4rem;
          margin: 0 auto 1.5rem;
          color: white;
        }

        /* Pricing Styles */
        .hdt-pricing-hero {
          background: linear-gradient(135deg, #E91E63 0%, #9C27B0 100%);
          padding: 4rem 0;
          color: white;
        }

        .hdt-pricing-title {
          color: white !important;
          font-size: 3rem !important;
          font-weight: 800 !important;
        }

        .hdt-pricing-badges, .hdt-security-feature-badges {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .hdt-badge-primary {
          background: #2196F3 !important;
          color: white !important;
        }

        .hdt-badge-success {
          background: #4CAF50 !important;
          color: white !important;
        }

        .hdt-badge-info {
          background: #00BCD4 !important;
          color: white !important;
        }

        /* Billing Toggle */
        .hdt-billing-toggle {
          padding: 2rem 0;
        }

        /* Service Pricing */
        .hdt-service-pricing-wrapper {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 16px rgba(0,0,0,0.1);
        }

        .hdt-service-header {
          padding: 3rem;
          text-align: center;
          color: white;
        }

        .hdt-service-icon-large {
          font-size: 5rem;
          margin-bottom: 1rem;
        }

        .hdt-pricing-card {
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          padding: 2rem;
          height: 100%;
          display: flex;
          flex-direction: column;
          transition: all 0.3s;
          position: relative;
        }

        .hdt-pricing-card-featured {
          border-width: 3px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
        }

        .hdt-plan-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: #666;
          color: white;
          padding: 0.4rem 1.5rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .hdt-plan-badge-popular {
          background: #E91E63;
          font-size: 0.9rem;
          padding: 0.5rem 2rem;
        }

        .hdt-plan-price {
          font-size: 2.5rem;
          font-weight: 800;
          color: #1e3c72;
          margin: 2rem 0 0.5rem;
          text-align: center;
        }

        .hdt-plan-annual-price {
          font-size: 0.9rem;
          color: #666;
          font-weight: 400;
        }

        .hdt-plan-limit {
          text-align: center;
          color: #666;
          margin-bottom: 2rem;
          font-weight: 600;
        }

        .hdt-feature-list {
          list-style: none;
          padding: 0;
          margin: 0 0 2rem 0;
          flex-grow: 1;
        }

        .hdt-feature-list li {
          padding: 0.5rem 0;
          display: flex;
          align-items: start;
          gap: 0.5rem;
        }

        .hdt-feature-list .icon {
          flex-shrink: 0;
          margin-top: 0.2rem;
        }

        /* Bundle Section */
        .hdt-bundle-section {
          background: linear-gradient(135deg, #F3E5F5 0%, #fff 100%);
          padding: 4rem 0;
        }

        .hdt-bundle-card {
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 16px;
          padding: 2rem;
          height: 100%;
          position: relative;
          transition: all 0.3s;
        }

        .hdt-bundle-featured {
          border-width: 4px;
          box-shadow: 0 12px 24px rgba(0,0,0,0.15);
        }

        .hdt-bundle-popular-badge {
          position: absolute;
          top: -15px;
          right: 20px;
          color: white;
          padding: 0.5rem 1.5rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 700;
          text-transform: uppercase;
        }

        .hdt-bundle-icon {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          margin: 0 auto 1.5rem;
          color: white;
        }

        .hdt-bundle-price-box {
          background: #f5f7fa;
          border-radius: 12px;
          padding: 1.5rem;
          margin: 1.5rem 0;
          text-align: center;
        }

        .hdt-bundle-regular-price {
          text-decoration: line-through;
          color: #999;
          font-size: 1.2rem;
        }

        .hdt-bundle-price {
          font-size: 2.5rem;
          font-weight: 800;
          color: #1e3c72;
          margin: 0.5rem 0;
        }

        .hdt-bundle-savings {
          font-weight: 700;
          font-size: 1.1rem;
        }

        .hdt-bundle-services {
          list-style: none;
          padding: 0;
          margin: 1.5rem 0;
        }

        .hdt-bundle-services li {
          padding: 0.5rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        /* FAQ Section */
        .hdt-faq-pricing {
          padding: 4rem 0;
        }

        .hdt-faq-box {
          border-left: 4px solid #2196F3;
          transition: transform 0.3s;
        }

        .hdt-faq-box:hover {
          transform: translateX(5px);
        }

        .hdt-faq-number {
          display: inline-block;
          width: 35px;
          height: 35px;
          background: #2196F3;
          color: white;
          border-radius: 50%;
          text-align: center;
          line-height: 35px;
          margin-right: 0.5rem;
          font-size: 1rem;
        }

        /* CTA Section */
        .hdt-cta-section {
          padding: 4rem 0;
        }

        .hdt-cta-box {
          background: linear-gradient(135deg, #2196F3 0%, #1e3c72 100%);
          color: white;
          padding: 4rem;
          border-radius: 16px;
        }

        .hdt-cta-box .title {
          color: white !important;
        }

        .hdt-cta-box .subtitle {
          color: #E3F2FD !important;
        }

        /* Responsive */
        @media screen and (max-width: 768px) {
          .hdt-hero-title {
            font-size: 2rem !important;
          }

          .hdt-pricing-title {
            font-size: 2rem !important;
          }

          .hdt-nav-content {
            flex-direction: column;
            gap: 1rem;
          }

          .hdt-nav-links {
            width: 100%;
            justify-content: center;
          }

          .hdt-floating-icons {
            grid-template-columns: 1fr;
            gap: 1rem;
            padding: 1rem;
          }

          .hdt-icon-circle {
            width: 100px;
            height: 100px;
            font-size: 3rem;
          }

          .hdt-stat-number {
            font-size: 2rem;
          }

          .hdt-plan-price {
            font-size: 2rem;
          }

          .hdt-bundle-price {
            font-size: 2rem;
          }

          .hdt-cta-box {
            padding: 2rem;
          }
        }
      `}</style>
    </div>
  );
}